/**
 * Pure derivations that turn raw settings data into the one-line summary
 * and status shown on each Settings Dashboard row. Kept free of React,
 * Redux and Supabase so every status rule is unit-testable.
 */

export type DirectoryStatus = 'ok' | 'attention' | 'failing' | 'unset';

export interface RowSummary {
  text: string;
  status: DirectoryStatus;
}

// Roles that run the back office rather than signing in at a till, so a
// missing passcode or policy assignment is not a gap for them.
const BACK_OFFICE_ROLES = new Set(['super', 'admin', 'super_admin']);

const plural = (count: number, one: string, many = `${one}s`) =>
  `${count} ${count === 1 ? one : many}`;

const formatRoleName = (role: string) => role.replace(/_/g, ' ');

const joinList = (items: string[]) => {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
};

export const formatRelativeTime = (value: string | null, now: Date) => {
  if (!value) return null;
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return null;
  const minutes = Math.round((now.getTime() - then.getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
};

const formatShortDay = (value: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

// ----------------------------------------------------------------
// Outlet & receipts
// ----------------------------------------------------------------

export interface OutletProfileInput {
  tin_number: string | null;
  phone: string | null;
  address: string | null;
  receipt_footnote: string | null;
}

export const getMissingProfileFields = (outlet: OutletProfileInput) =>
  [
    !outlet.tin_number?.trim() && 'TIN',
    !outlet.phone?.trim() && 'phone',
    !outlet.address?.trim() && 'address',
    !outlet.receipt_footnote?.trim() && 'receipt footnote',
  ].filter(Boolean) as string[];

export const summarizeOutletProfile = (
  outlet: OutletProfileInput,
): RowSummary => {
  const missing = getMissingProfileFields(outlet);
  if (missing.length === 0) {
    return {
      text: 'TIN, phone, address and footnote print on receipts',
      status: 'ok',
    };
  }
  return {
    text: `Missing ${joinList(missing)} on receipts`,
    status: 'attention',
  };
};

export const summarizeSections = (input: {
  sections: number;
  tables: number;
}): RowSummary => {
  if (input.sections === 0) {
    return { text: 'No sections or tables yet', status: 'unset' };
  }
  return {
    text: `${plural(input.sections, 'section')} · ${plural(input.tables, 'table')}`,
    status: 'ok',
  };
};

// ----------------------------------------------------------------
// People & access
// ----------------------------------------------------------------

export interface StaffMemberInput {
  role: string;
  is_active: boolean;
  has_passcode: boolean;
}

export const summarizeStaff = (input: {
  outletStaff: StaffMemberInput[];
  limits: { maxUsers: number; currentUsers: number } | null;
}): RowSummary => {
  const active = input.outletStaff.filter((user) => user.is_active);
  const withoutPasscode = active.filter(
    (user) => !BACK_OFFICE_ROLES.has(user.role) && !user.has_passcode,
  ).length;

  const parts: string[] = [];
  let status: DirectoryStatus = 'ok';

  if (input.limits && Number.isFinite(input.limits.maxUsers)) {
    const { currentUsers, maxUsers } = input.limits;
    parts.push(`${currentUsers} of ${maxUsers} seats used`);
    if (maxUsers > 0 && currentUsers / maxUsers >= 0.9) status = 'attention';
  } else {
    parts.push(plural(active.length, 'active account'));
  }

  if (withoutPasscode > 0) {
    parts.push(`${withoutPasscode} without a passcode`);
    status = 'attention';
  }

  return { text: parts.join(' · '), status };
};

export const summarizeRoles = (input: {
  policiesCount: number;
  outletRoles: string[];
  assignedRoles: string[];
}): RowSummary => {
  const assigned = new Set(input.assignedRoles);
  const onDefaults = Array.from(new Set(input.outletRoles))
    .filter((role) => !BACK_OFFICE_ROLES.has(role) && !assigned.has(role))
    .map(formatRoleName)
    .sort();

  if (input.policiesCount === 0) {
    return {
      text: 'No custom policies · every role uses its defaults',
      status: 'ok',
    };
  }

  const policies = plural(input.policiesCount, 'policy', 'policies');
  if (onDefaults.length === 0) {
    return { text: `${policies} · every role assigned`, status: 'ok' };
  }
  return {
    text: `${policies} · ${joinList(onDefaults)} on defaults`,
    status: 'ok',
  };
};

export const summarizeDevices = (
  devices: { is_enabled: boolean }[],
): RowSummary => {
  if (devices.length === 0) {
    return { text: 'No devices registered yet', status: 'unset' };
  }
  const enabled = devices.filter((device) => device.is_enabled).length;
  const disabled = devices.length - enabled;
  return {
    text:
      disabled > 0
        ? `${enabled} enabled · ${disabled} blocked`
        : `${plural(enabled, 'device')} enabled`,
    status: 'ok',
  };
};

// ----------------------------------------------------------------
// Hardware
// ----------------------------------------------------------------

export interface PrinterRoutingInput {
  printers: { is_active: boolean }[];
  sections: { id: string }[];
  kotDepartments: { id: string }[];
  mappings: {
    section_id: string;
    operation: string;
    department_id: string | null;
  }[];
}

export const countMissingPrinterRoutes = (input: PrinterRoutingInput) => {
  const billSections = new Set(
    input.mappings
      .filter((mapping) => mapping.operation === 'BILL')
      .map((mapping) => mapping.section_id),
  );
  const kotRoutes = new Set(
    input.mappings
      .filter((mapping) => mapping.operation === 'KOT')
      .map((mapping) => `${mapping.section_id}:${mapping.department_id}`),
  );

  let missing = 0;
  input.sections.forEach((section) => {
    if (!billSections.has(section.id)) missing += 1;
    input.kotDepartments.forEach((department) => {
      if (!kotRoutes.has(`${section.id}:${department.id}`)) missing += 1;
    });
  });
  return missing;
};

export const summarizePrinters = (input: PrinterRoutingInput): RowSummary => {
  const active = input.printers.filter((printer) => printer.is_active).length;
  if (input.printers.length === 0) {
    return { text: 'No printers added', status: 'unset' };
  }

  const missing = countMissingPrinterRoutes(input);
  const inactive = input.printers.length - active;
  const parts = [`${active} active`];
  if (inactive > 0) parts.push(`${inactive} switched off`);
  if (missing > 0) {
    parts.push(`${plural(missing, 'route')} without a printer`);
  }

  return { text: parts.join(' · '), status: missing > 0 ? 'attention' : 'ok' };
};

// ----------------------------------------------------------------
// Fiscal (RRA EBM)
// ----------------------------------------------------------------

export interface EbmDeviceInput {
  activation_status: 'NOT_CONFIGURED' | 'PENDING_INIT' | 'ACTIVE' | 'FAILED';
  last_health_check_at: string | null;
  last_health_ok: boolean | null;
}

export const summarizeEbmDevice = (
  config: EbmDeviceInput | null,
  now: Date,
): RowSummary => {
  if (!config || config.activation_status === 'NOT_CONFIGURED') {
    return { text: 'No fiscal device configured', status: 'unset' };
  }
  if (config.activation_status === 'FAILED') {
    return {
      text: 'Activation failed · re-run device setup',
      status: 'failing',
    };
  }
  if (config.activation_status === 'PENDING_INIT') {
    return {
      text: 'Saved · waiting for device activation',
      status: 'attention',
    };
  }

  const checked = formatRelativeTime(config.last_health_check_at, now);
  if (config.last_health_ok === false) {
    return {
      text: `Active · last health check failed${checked ? ` ${checked}` : ''}`,
      status: 'failing',
    };
  }
  return {
    text: checked ? `Active · healthy ${checked}` : 'Active',
    status: 'ok',
  };
};

export const summarizeFiscalItems = (missingCount: number): RowSummary =>
  missingCount > 0
    ? {
        text: `${plural(missingCount, 'menu item')} missing a tax category or item code`,
        status: 'attention',
      }
    : {
        text: 'Every menu item has a tax category and item code',
        status: 'ok',
      };

export const summarizeSaleSubmissions = (input: {
  failed: number;
  pending: number;
}): RowSummary => {
  if (input.failed > 0) {
    return {
      text: `${input.failed} failed${input.pending > 0 ? ` · ${input.pending} waiting` : ''}`,
      status: 'failing',
    };
  }
  if (input.pending > 0) {
    return { text: `${input.pending} waiting to confirm`, status: 'ok' };
  }
  return { text: 'All sales confirmed by RRA', status: 'ok' };
};

// ----------------------------------------------------------------
// Notifications
// ----------------------------------------------------------------

export const summarizeEmailRecipients = (
  configs: { is_active: boolean; events: string[] }[],
): RowSummary => {
  const active = configs.filter((config) => config.is_active);
  if (active.length === 0) {
    return { text: 'Nobody receives report emails', status: 'unset' };
  }
  const events = new Set(active.flatMap((config) => config.events)).size;
  return {
    text: `${plural(active.length, 'recipient')} · ${plural(events, 'report type')}`,
    status: 'ok',
  };
};

// ----------------------------------------------------------------
// Plan & billing
// ----------------------------------------------------------------

export interface SubscriptionInput {
  package: string;
  subscription_status: string;
  current_period_end: string | null;
  trial_ends_at: string | null;
  cancel_at_period_end: boolean;
  grace_period_ends_at: string | null;
  suspended_at: string | null;
}

export const summarizeSubscription = (
  billing: SubscriptionInput | null,
): RowSummary => {
  if (!billing) {
    return { text: 'No active subscription', status: 'unset' };
  }

  const plan = billing.package.replace(/_/g, ' ');
  const planName = plan.charAt(0).toUpperCase() + plan.slice(1);

  if (billing.suspended_at) {
    return { text: `${planName} · suspended until payment`, status: 'failing' };
  }
  if (
    billing.subscription_status === 'past_due' ||
    billing.subscription_status === 'incomplete'
  ) {
    const graceEnds = formatShortDay(billing.grace_period_ends_at);
    return {
      text: `${planName} · payment due${graceEnds ? ` before ${graceEnds}` : ''}`,
      status: 'failing',
    };
  }
  if (billing.subscription_status === 'canceled') {
    return { text: `${planName} · canceled`, status: 'failing' };
  }
  if (billing.subscription_status === 'trialing') {
    const trialEnds = formatShortDay(billing.trial_ends_at);
    return {
      text: `${planName} trial${trialEnds ? ` · ends ${trialEnds}` : ''}`,
      status: 'attention',
    };
  }

  const periodEnd = formatShortDay(billing.current_period_end);
  if (billing.cancel_at_period_end) {
    return {
      text: `${planName} · cancels${periodEnd ? ` ${periodEnd}` : ' at period end'}`,
      status: 'attention',
    };
  }
  return {
    text: `${planName}${periodEnd ? ` · renews ${periodEnd}` : ''}`,
    status: 'ok',
  };
};

export const needsAttention = (status: DirectoryStatus | undefined) =>
  status === 'attention' || status === 'failing';
